import React from 'react'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {MBContentSubTitleTypography} from '../../../Atoms/Typographies'
import {MBRequiredBadge} from '../../../Atoms/Badges'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {FlexContainer} from '../../../Atoms/Containers'
import {MBMemberInput} from '../../../Atoms/MBMemberInput'
import {MBAddressSearchButton} from '../../../Atoms/Buttons'
import {ErrorDisplay} from '../../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MBMemberSelect} from '../../../Atoms/MBMemberSelect'
import {ADDRESS} from '../../../../../Common/Constants'
import {SearchPostCode} from '../../../../../Common/SearchPostCode'
import {Address} from '../../../../DTO/MemberAddressDTO'
import {convertOnBlur} from '../../../../Utils/ConvertOnBlur'
import {useTranslation} from 'react-i18next'

export type InputMode = 'signup' | 'edit'

export type AddressErrors = {
  postCodeErrors: string[]
  addressPrefectureCodeErrors: string[]
  addressCityErrors: string[]
  addressStreetErrors: string[]
  addressBuildingErrors: string[]
}

export type AddressProps = {
  activity: InputMode
  searchPostCode: SearchPostCode
  address: Address
  addressErrors: AddressErrors
  onChange: (address: Address) => void
}
export const AddressInputs: React.VFC<AddressProps> = (
  {
    activity,
    searchPostCode,
    address,
    addressErrors,
    onChange,
  },
) => {
  const {t} = useTranslation('member')
  const updateAddress = (name: string, value: string, address: Address): Address => ({
    ...address,
    [name]: value,
  })

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    address: Address,
  ): Address => {
    const {name, value} = e.target
    return updateAddress(name, value, address)
  }

  const handleChangeSelect = (
    e: React.ChangeEvent<HTMLSelectElement>,
    address: Address,
  ): Address => {
    const {name, value} = e.target
    return updateAddress(name, value, address)
  }

  const handleBlurInput = (e: React.FocusEvent<HTMLInputElement>, address: Address): Address => {
    const {name, value} = e.target
    return updateAddress(name, convertOnBlur(name, value), address)
  }

  const settingPostCode = (address: Address): void => {
    if (address.postCode.length !== 7) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchPostCode.search(address.postCode, (searchAddress: any) => {
      const regionId = searchAddress.region_id.toString()
      address = {
        ...address,
        addressPrefectureCode: (regionId) ? regionId.padStart(2, '0') : '',
        addressCity: `${searchAddress.locality}${searchAddress.street}`,
      }
      if (activity === 'edit') {
        address = {
          ...address,
          addressStreet: '',
          addressBuilding: '',
        }
      }
      onChange(address)
    })
  }

  return (
    <PIMStack
      spacing={'18px'}
      divider={<MBContentBodyDivider/>}
    >
      <div data-testid={'form-post-code'}>
        <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
          <label htmlFor="post-code-input">
            <MBContentSubTitleTypography>郵便番号</MBContentSubTitleTypography>
          </label>
          <MBRequiredBadge/>
        </PIMStack>
        <Spacer mt={'10px'}>
          <FlexContainer alignItems={'center'} justifyContent={'flex-start'} gap={'9px'}>
            <MBMemberInput
              type="tel"
              name="postCode"
              autoComplete="postal-code"
              value={address.postCode}
              className="form-text_01 w_120px mr_10"
              id="post-code-input"
              placeholder="例）1234567"
              hasError={addressErrors.postCodeErrors.length > 0}
              onChange={(e): void => {
                onChange(
                  handleChangeInput(e, address),
                )
              }}
              onBlur={(e): void => {
                onChange(
                  handleBlurInput(e, address),
                )
              }}
            />
            <MBAddressSearchButton
              onClick={(): void => {
                settingPostCode(address)
              }}
            />
          </FlexContainer>
          <ErrorDisplay errors={addressErrors.postCodeErrors}/>
        </Spacer>
      </div>

      <div data-testid={'form-address-prefecture'}>
        <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
          <label htmlFor="address-prefecture-select">
            <MBContentSubTitleTypography>都道府県</MBContentSubTitleTypography>
          </label>
        </PIMStack>
        <Spacer mt={'10px'}>
          <MBMemberSelect
            id="address-prefecture-select"
            name="addressPrefectureCode"
            autoComplete="address-level1"
            value={address.addressPrefectureCode || ''}
            width={'60%'}
            hasError={addressErrors.addressPrefectureCodeErrors.length > 0}
            onChange={(e): void => {
              onChange(
                handleChangeSelect(e, address),
              )
            }}
          >
            <option value="">選択してください</option>
            {Object.keys(ADDRESS.PREFECTURES).sort().map(code => (
              <option key={code} value={code}>{ADDRESS.PREFECTURES[code]}</option>),
            )}
          </MBMemberSelect>
          <ErrorDisplay errors={addressErrors.addressPrefectureCodeErrors}/>
        </Spacer>
      </div>

      <div data-testid={'form-address-city'}>
        <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
          <label htmlFor="address-city-input">
            <MBContentSubTitleTypography>市区町村</MBContentSubTitleTypography>
          </label>
        </PIMStack>
        <Spacer mt={'10px'}>
          <MBMemberInput
            type="text"
            name="addressCity"
            autoComplete="address-level2"
            value={address.addressCity || ''}
            className="form-text_01 w_100"
            id="address-city-input"
            placeholder={t('入力フォームプレースホルダ.住所.市区町村')}
            hasError={addressErrors.addressCityErrors.length > 0}
            onChange={(e): void => {
              onChange(
                handleChangeInput(e, address),
              )
            }}
            onBlur={(e): void => {
              onChange(
                handleBlurInput(e, address),
              )
            }}
          />
          <ErrorDisplay errors={addressErrors.addressCityErrors}/>
        </Spacer>
      </div>

      <div data-testid={'form-address-street'}>
        <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
          <label htmlFor="address-street-input">
            <MBContentSubTitleTypography>以降の住所</MBContentSubTitleTypography>
          </label>
        </PIMStack>
        <Spacer mt={'10px'}>
          <MBMemberInput
            type="text"
            name="addressStreet"
            autoComplete="address-line1"
            value={address.addressStreet || ''}
            className="form-text_01 w_100 pc100"
            id="address-street-input"
            placeholder={t('入力フォームプレースホルダ.住所.以降の住所')}
            hasError={addressErrors.addressStreetErrors.length > 0}
            onChange={(e): void => {
              onChange(
                handleChangeInput(e, address),
              )
            }}
            onBlur={(e): void => {
              onChange(
                handleBlurInput(e, address),
              )
            }}
          />
          <ErrorDisplay errors={addressErrors.addressStreetErrors}/>
        </Spacer>
      </div>

      <div data-testid={'form-address-building'}>
        <label htmlFor="address-building-input">
          <MBContentSubTitleTypography>建物・部屋</MBContentSubTitleTypography>
        </label>
        <Spacer mt={'10px'}>
          <MBMemberInput
            type="text"
            name="addressBuilding"
            autoComplete="address-line2"
            value={address.addressBuilding || ''}
            className="form-text_01 w_100 pc100"
            id="address-building-input"
            placeholder={t('入力フォームプレースホルダ.住所.建物・部屋')}
            hasError={addressErrors.addressBuildingErrors.length > 0}
            onChange={(e): void => {
              onChange(
                handleChangeInput(e, address),
              )
            }}
            onBlur={(e): void => {
              onChange(
                handleBlurInput(e, address),
              )
            }}
          />
          <ErrorDisplay errors={addressErrors.addressBuildingErrors}/>
        </Spacer>
      </div>
    </PIMStack>
  )
}
